import { GlobalOutlined } from "@ant-design/icons";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import SearchIcon from "@mui/icons-material/Search";
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import axiosInstance from "../../../axios/axios-instance";
import ActionButton from "../../../components/common/buttons/ActionButton";
import CancelButton from "../../../components/common/buttons/CancelButton";
import Card from "../../../components/common/Card";
import EndpointTranslationModal from "../../../components/common/EndpointTranslationModal";
import MuiTable from "../../../components/common/MuiTable";
import SearchInput from "../../../components/common/fields/SearchInput";
import { useLanguages } from "../../../hooks/useLanguages";
import { Column } from "../../../utils/constants";
import { pathAPI } from "../../../utils/pathApi";
import CancelReasonDialog from "./CancelReasonDialog";
import { CancelReason, CancelReasonEnvelope } from "./cancelReason.types";

type AlertSeverity = "success" | "error" | "info" | "warning";

const DEFAULT_LANGUAGE_CODE = "en";

/** Rows sharing a non-null code collapse into one group; null-code rows stay singletons. */
interface CancelReasonGroup {
  representative: CancelReason;
  rowIds: number[];
}

/**
 * One row per reason: same non-null code -> one group whose representative is the
 * row with the MINIMUM id (source row, saved first). Null code -> singleton group.
 */
const groupByCode = (rows: CancelReason[]): CancelReasonGroup[] => {
  const order: string[] = [];
  const buckets = new Map<string, CancelReason[]>();
  rows.forEach((row) => {
    const code = row.code?.trim();
    const key = code ? `code:${code}` : `row:${row.id}`;
    const bucket = buckets.get(key);
    if (bucket) {
      bucket.push(row);
    } else {
      buckets.set(key, [row]);
      order.push(key);
    }
  });
  return order.map((key) => {
    const bucket = [...(buckets.get(key) ?? [])].sort((a, b) => a.id - b.id);
    return { representative: bucket[0], rowIds: bucket.map((item) => item.id) };
  });
};

const CancelReasons = () => {
  const [{ data: languages }] = useLanguages();

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CancelReason[]>([]);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");

  const [openDialog, setOpenDialog] = useState(false);
  const [currentReason, setCurrentReason] = useState<CancelReason | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CancelReason | null>(null);
  const [deleting, setDeleting] = useState<number | null>(null);

  const [translationModalVisible, setTranslationModalVisible] = useState(false);
  const [selectedReason, setSelectedReason] = useState<CancelReason | null>(null);

  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success" as AlertSeverity,
  });

  const showAlert = (message: string, severity: AlertSeverity) => {
    setAlert({ open: true, message, severity });
  };

  const handleCloseAlert = () => {
    setAlert((prev) => ({ ...prev, open: false }));
  };

  const fetchCancelReasons = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get<CancelReasonEnvelope | CancelReason[]>(
        pathAPI.cancelReason
      );
      const body = response.data;
      const rows = Array.isArray(body) ? body : Array.isArray(body?.data) ? body.data : [];
      setData(rows);
    } catch (error) {
      console.error("Error fetching cancel reasons:", error);
      showAlert("Failed to fetch cancel reasons", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCancelReasons();
  }, []);

  // Default source language for CREATE only: stored UI language -> 'en' -> first -> 0.
  const defaultSourceLanguageId = useMemo(() => {
    const stored = localStorage.getItem("language");
    const preferred =
      languages?.find((lang) => lang.language_code === stored) ||
      languages?.find((lang) => lang.language_code === DEFAULT_LANGUAGE_CODE) ||
      languages?.[0];
    return preferred?.id ?? 0;
  }, [languages]);

  const groups = useMemo(() => groupByCode(data), [data]);

  const groupsById = useMemo(() => {
    const map = new Map<number, CancelReasonGroup>();
    groups.forEach((group) => map.set(group.representative.id, group));
    return map;
  }, [groups]);

  // Client-side search on the representative row's code OR message.
  const visibleRepresentatives = useMemo(() => {
    const term = appliedKeyword.trim().toLowerCase();
    const representatives = groups.map((group) => group.representative);
    if (!term) return representatives;
    return representatives.filter(
      (row) =>
        (row.code ?? "").toLowerCase().includes(term) ||
        (row.message ?? "").toLowerCase().includes(term)
    );
  }, [groups, appliedKeyword]);

  const handleSearch = () => {
    setAppliedKeyword(searchKeyword);
  };

  const handleAdd = () => {
    setCurrentReason(null);
    setOpenDialog(true);
  };

  const handleEdit = (reason: CancelReason) => {
    setCurrentReason(reason);
    setOpenDialog(true);
  };

  const handleFormSaved = (message: string) => {
    showAlert(message, "success");
    fetchCancelReasons();
  };

  const handleManageTranslations = (reason: CancelReason) => {
    setSelectedReason(reason);
    setTranslationModalVisible(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const rowIds = groupsById.get(deleteTarget.id)?.rowIds ?? [deleteTarget.id];
    try {
      setDeleting(deleteTarget.id);
      let failed = false;
      // No bulk endpoint: remove every row of the group sequentially.
      for (const rowId of rowIds) {
        try {
          await axiosInstance.delete(`${pathAPI.cancelReason}/${rowId}`);
        } catch (error) {
          console.error(`Error deleting cancel reason row ${rowId}:`, error);
          failed = true;
        }
      }
      if (failed) {
        showAlert("Failed to delete cancel reason", "error");
      } else {
        showAlert("Cancel reason deleted successfully", "success");
      }
      setDeleteTarget(null);
      fetchCancelReasons();
    } finally {
      setDeleting(null);
    }
  };

  const header: Column[] = [
    { id: "code", label: "Code", minWidth: 180, align: "center" },
    { id: "message", label: "Message", minWidth: 300, align: "center" },
    {
      id: "action",
      label: "Actions",
      minWidth: 120,
      align: "center",
      format: (_value: unknown, row?: CancelReason) => {
        if (!row) return null;
        const isDeleting = deleting === row.id;
        return (
          <Box display="flex" justifyContent="center" alignItems="center" gap={2}>
            <IconButton
              size="small"
              onClick={() => handleManageTranslations(row)}
              title="Translations"
            >
              <GlobalOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
            </IconButton>
            <IconButton size="small" onClick={() => handleEdit(row)} title="Edit">
              <EditIcon color="success" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => setDeleteTarget(row)}
              disabled={isDeleting}
              title="Delete"
            >
              {isDeleting ? <CircularProgress size={20} /> : <DeleteIcon color="error" />}
            </IconButton>
          </Box>
        );
      },
    },
  ];

  return (
    <Card>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3, flexWrap: "wrap", gap: 2 }}>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <Box width={300}>
            <SearchInput
              placeholder="Search cancel reasons..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onSearch={handleSearch}
            />
          </Box>
          <ActionButton
            appSize="large"
            startIcon={<SearchIcon />}
            onClick={handleSearch}
            disabled={loading}
          >
            Search
          </ActionButton>
        </Box>
        <ActionButton appSize="large" startIcon={<AddIcon />} onClick={handleAdd}>
          Add New
        </ActionButton>
      </Box>

      <MuiTable header={header} data={visibleRepresentatives} isLoading={loading} />

      <CancelReasonDialog
        open={openDialog}
        reason={currentReason}
        languageId={defaultSourceLanguageId}
        onClose={() => setOpenDialog(false)}
        onSaved={handleFormSaved}
        onError={(message) => showAlert(message, "error")}
      />

      <Dialog
        open={deleteTarget !== null}
        onClose={() => !deleting && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Cancel Reason</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{deleteTarget?.message}&quot;? This removes
            the cancel reason from all languages.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <CancelButton onClick={() => setDeleteTarget(null)} disabled={deleting !== null}>
            Cancel
          </CancelButton>
          <ActionButton onClick={handleConfirmDelete} disabled={deleting !== null}>
            {deleting !== null ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </ActionButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={alert.open}
        autoHideDuration={6000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
          {alert.message}
        </Alert>
      </Snackbar>

      {/* Translation Modal - For handling cancel reason translations */}
      <EndpointTranslationModal
        open={translationModalVisible}
        onClose={() => setTranslationModalVisible(false)}
        endpoint={`${pathAPI.cancelReason}/${selectedReason?.id}/translations`}
        title="Manage Translations"
        subtitle={selectedReason?.code ?? ""}
        fallbackSourceText={selectedReason?.message}
      />
    </Card>
  );
};

export default CancelReasons;
